import { useEffect, useRef } from "react";

/** Matches docs/03_SECURITY.md §5.1 — idle logout after 15 minutes. */
export const IDLE_TIMEOUT_MS = 15 * 60 * 1000;

/** How often to compare last activity against the idle limit. */
export const IDLE_CHECK_MS = 15_000;

const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "wheel",
  "pointerdown",
] as const;

/**
 * Calls `onIdle` once the user has produced no real input for `timeoutMs`.
 * Disabled when `enabled` is false (anonymous / loading). Activity listeners
 * are passive so they cannot block scrolling; the timer is the only thing
 * that decides idle — not visibility alone (a hidden tab still ages out).
 */
export function useIdleTimeout(
  enabled: boolean,
  onIdle: () => void,
  timeoutMs: number = IDLE_TIMEOUT_MS,
): void {
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  useEffect(() => {
    if (!enabled) return;

    let lastActivity = Date.now();
    let fired = false;

    const markActivity = () => {
      lastActivity = Date.now();
    };

    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, markActivity, { passive: true });
    }

    const interval = window.setInterval(() => {
      if (fired) return;
      if (Date.now() - lastActivity < timeoutMs) return;
      fired = true;
      onIdleRef.current();
    }, Math.min(IDLE_CHECK_MS, timeoutMs));

    return () => {
      window.clearInterval(interval);
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, markActivity);
      }
    };
  }, [enabled, timeoutMs]);
}
